/**
 * The classification lookups an admin maintains in Settings › Classification.
 *
 * Bloom's and Difficulty are lists of levels: a label, a description, and
 * whether authors are offered it. An admin can add their own types the same
 * way (a clinical setting, a question source, a lecture), each with its own
 * levels. The question editor reads these lists, so what changes here changes
 * what every author sees.
 *
 * Difficulty keeps its three levels (the stored values are fixed); their
 * labels, descriptions and availability are the admin's. Bloom's and custom
 * types can grow.
 */

import { AUTHORING_BLOOM_OPTIONS, AUTHORING_DIFFICULTY_OPTIONS } from "@/lib/library-authoring"

export interface LookupLevel {
  /** What a question stores. Never changes once questions use it. */
  value: string
  label: string
  description: string
  active: boolean
  /**
   * On a scale, the number this level stands for (Difficulty's Low might be
   * 0.1, High 0.7). The admin's to set; absent until they do.
   */
  score?: number
}

/**
 * How a type's values are shaped. A list is one row of levels in order, like
 * Bloom's. A tree is categories with sub-categories, like Content; its rows
 * live in the category store under its own dimension.
 */
export type ClassificationStructure = "list" | "tree"

/** Marks the dimension behind a tree-shaped type, so it is not listed as a standard. */
export const CUSTOM_TREE_DIMENSION_KIND = "classification-type"

export function isCustomTypeDimension(dimension: { kind?: string }): boolean {
  return dimension.kind === CUSTOM_TREE_DIMENSION_KIND
}

/**
 * What sort of classification a type the admin added is. It decides how its
 * values are kept in Settings and how an author picks them on a question:
 *
 *   scale    Ordered levels, pick one: Bloom's, Difficulty.
 *   choice   Options in no order, pick one: question source.
 *   tags     Pick any number: clinical setting, body system.
 *   tree     Categories with sub-categories, pick one: Content.
 *
 * A standard (a tree the program reports on, mapped to content) is not a
 * custom type; New type adds it to Standards instead.
 */
export type ClassificationKind = "scale" | "choice" | "tags" | "tree"

/** A type the admin added: a name, what it is for, and its levels or its tree. */
export interface CustomClassificationType {
  id: string
  name: string
  description: string
  /** Absent on types saved before kinds existed: read from `structure`. */
  kind?: ClassificationKind
  /** Absent on types saved before trees existed: a list. */
  structure?: ClassificationStructure
  /** A tree's dimension in the category store. */
  dimensionId?: string
  levels: LookupLevel[]
}

export function kindOf(type: Pick<CustomClassificationType, "kind" | "structure">): ClassificationKind {
  return type.kind ?? (type.structure === "tree" ? "tree" : "scale")
}

export function structureOf(type: Pick<CustomClassificationType, "kind" | "structure">): ClassificationStructure {
  return kindOf(type) === "tree" ? "tree" : "list"
}

/**
 * A tags type keeps every value it holds in the one string a question stores
 * per type, joined by this. Level values come from labels, which never hold it.
 */
const TAG_SEPARATOR = "|"

export function tagValues(stored: string | undefined): string[] {
  return stored ? stored.split(TAG_SEPARATOR).filter(Boolean) : []
}

export function joinTags(values: readonly string[]): string {
  return values.join(TAG_SEPARATOR)
}

export interface ClassificationLookups {
  bloom: LookupLevel[]
  difficulty: LookupLevel[]
  custom: CustomClassificationType[]
  /** The order types are shown in (overview and panel), by scope. Missing ones go last. */
  typeOrder?: string[]
}

/** Which list of levels: Bloom's, Difficulty, or a custom type's id. */
export type LevelListKey = "bloom" | "difficulty" | string

export function defaultClassificationLookups(): ClassificationLookups {
  return {
    bloom: AUTHORING_BLOOM_OPTIONS.map((option) => ({
      value: option.value,
      label: option.label,
      description: option.hint,
      active: true,
    })),
    difficulty: AUTHORING_DIFFICULTY_OPTIONS.map((option) => ({
      value: option.value,
      label: option.label,
      description: option.description,
      active: true,
    })),
    custom: [],
  }
}

export function levelsOf(lookups: ClassificationLookups, key: LevelListKey): LookupLevel[] {
  if (key === "bloom") return lookups.bloom
  if (key === "difficulty") return lookups.difficulty
  return (lookups.custom ?? []).find((type) => type.id === key)?.levels ?? []
}

/** The same lookups with one list's levels replaced. */
export function withLevels(lookups: ClassificationLookups, key: LevelListKey, levels: LookupLevel[]): ClassificationLookups {
  if (key === "bloom") return { ...lookups, bloom: levels }
  if (key === "difficulty") return { ...lookups, difficulty: levels }
  return {
    ...lookups,
    custom: (lookups.custom ?? []).map((type) => (type.id === key ? { ...type, levels } : type)),
  }
}

/** `items` in the saved order; anything not in it keeps its place after them. */
export function inTypeOrder<T extends { scope: string }>(items: readonly T[], order: readonly string[] | undefined): T[] {
  if (!order?.length) return [...items]
  const rank = new Map(order.map((scope, index) => [scope, index]))
  return [...items].sort((a, b) => (rank.get(a.scope) ?? order.length + items.indexOf(a)) - (rank.get(b.scope) ?? order.length + items.indexOf(b)))
}

/** The list with one entry moved one place up or down. */
export function moved<T>(list: readonly T[], index: number, direction: "up" | "down"): T[] {
  const to = direction === "up" ? index - 1 : index + 1
  if (index < 0 || to < 0 || to >= list.length) return [...list]
  const next = [...list]
  ;[next[index], next[to]] = [next[to]!, next[index]!]
  return next
}

/** The list with one entry taken from `from` and put at `to` (drag and drop). */
export function reordered<T>(list: readonly T[], from: number, to: number): T[] {
  if (from < 0 || to < 0 || from >= list.length || to >= list.length || from === to) return [...list]
  const next = [...list]
  const [item] = next.splice(from, 1)
  next.splice(to, 0, item!)
  return next
}

/** The levels authors are offered, as picker options. */
export function activeOptions(levels: readonly LookupLevel[]): { value: string; label: string }[] {
  return levels.filter((level) => level.active).map((level) => ({ value: level.value, label: level.label }))
}

/** A new level's stored value: its label, made unique among the rest. */
export function newLevelValue(levels: readonly LookupLevel[], label: string): string {
  const base = label.trim().split(TAG_SEPARATOR).join("/") || "Level"
  let value = base
  let n = 2
  while (levels.some((level) => level.value.toLowerCase() === value.toLowerCase())) value = `${base} ${n++}`
  return value
}

/** A level to add: a name, and on a scale the value it stands for. */
export interface NewLevel {
  label: string
  score?: number
}

/**
 * The levels with new ones added. On a scale a new level with a value goes
 * where its value fits (before the first level valued higher); without one, or
 * on any other kind, it goes last. Blank names are skipped.
 */
export function withNewLevels(levels: readonly LookupLevel[], added: readonly NewLevel[], byScore: boolean): LookupLevel[] {
  return placeNewLevels(levels, added, byScore).levels
}

/**
 * `withNewLevels`, also handing back each new level in the order they were
 * typed (`null` for a blank name), so a preview can say where each one lands.
 */
export function placeNewLevels(
  levels: readonly LookupLevel[],
  added: readonly NewLevel[],
  byScore: boolean,
): { levels: LookupLevel[]; created: (LookupLevel | null)[] } {
  const next = [...levels]
  const created: (LookupLevel | null)[] = []
  for (const row of added) {
    const label = row.label.trim()
    if (!label) {
      created.push(null)
      continue
    }
    const level: LookupLevel = {
      value: newLevelValue(next, label),
      label,
      description: "",
      active: true,
      ...(row.score !== undefined ? { score: row.score } : {}),
    }
    const score = row.score
    const at = byScore && score !== undefined ? next.findIndex((l) => l.score !== undefined && l.score > score) : -1
    next.splice(at < 0 ? next.length : at, 0, level)
    created.push(level)
  }
  return { levels: next, created }
}

/** A new type's id: its name as a slug, unique among the rest. */
export function newTypeId(types: readonly CustomClassificationType[], name: string): string {
  const base = `type-${name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "custom"}`
  let id = base
  let n = 2
  while (types.some((type) => type.id === id)) id = `${base}-${n++}`
  return id
}
